import {
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_TOO_MANY_REQUESTS,
} from "../constants/app";
import {
  BYTES_PER_MEBIBYTE,
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_PAYLOAD_TOO_LARGE,
  MAX_SKILL_BUNDLE_ARCHIVE_BYTES,
  ORGANIZATION_SKILL_MESSAGE_KEY_PREFIX,
  SKILL_ERROR_MESSAGE_KEYS,
  SKILL_SHARE_MESSAGE_KEY_PREFIX,
  SKILL_SOURCE_MESSAGE_KEY_SUFFIX_PREFIX,
} from "../constants/skills";
import type {
  IBuildSkillAnalyticsEventProps,
  IResolveConflictMessageKeyProps,
  IResolveForbiddenKeyProps,
  IResolveMessageKeyProps,
  ISafeSkillError,
  ISanitizeSkillErrorProps,
  ISkillAnalyticsEvent,
} from "../types/skill-ui";
import type { TSkillIssueArgs } from "../types/skills";
import {
  EOrganizationSkillErrorCode,
  ESkillShareErrorCode,
  ESkillSourceErrorCode,
} from "../types/skills";
import { isAbortError } from "./abort-error";
import { isRetryableStatus, readErrorEnvelope } from "./http-retry";

// NOTE: NSAI-3306/NSAI-3307 — the boundary deciding what a Skills failure may
// tell a user, and what a Skills event may tell analytics.
//
// One rule both halves enforce: a client NEVER renders or reports server prose,
// a signed URL, an S3 key, a repository credential, a resource path, skill
// instructions or file bytes. A skills error body can legitimately quote a
// resource path, and a presign response is a URL with credentials in its query
// string, so "show the server's message" is unsafe by default. Everything here
// resolves to an i18n key this side owns.

// NOTE: NSAI-4961 — a GitHub source failure arrives as an HTTP error carrying
// `errorCode`, not as preview data, so without this it would collapse into the
// generic "something went wrong" and a user importing a private repository would
// never learn that a connection is what they are missing.
//
// Only a value that IS a member of ESkillSourceErrorCode is mapped, and it is
// mapped to a key in this package's own mirrored catalogue — an unrecognized code
// falls through rather than being interpolated into a key.
const resolveSourceErrorMessageKey = (
  errorCode: string | null,
): string | null => {
  if (errorCode === null) {
    return null;
  }

  // NOTE: The cast widens the enum's own literal union to string so `includes` will
  // accept an arbitrary server-supplied code. Without it TypeScript rejects the
  // argument as not being one of the enum members — which is precisely the question
  // being asked. It only widens a type, never invents a value.
  const isKnownSourceCode = (
    Object.values(ESkillSourceErrorCode) as string[]
  ).includes(errorCode);

  return isKnownSourceCode
    ? `${SKILL_SOURCE_MESSAGE_KEY_SUFFIX_PREFIX}${errorCode.toLowerCase()}`
    : null;
};

// NOTE: NSAI-3309/NSAI-3311 — the sharing and catalog codes resolve exactly the
// way the source codes do: membership in the enum is what makes a code renderable,
// so a code the server invents later falls through to the status map instead of
// producing a missing translation. The enum member is the i18n leaf, lower-cased.
const resolveEnumErrorMessageKey = ({
  errorCode,
  knownCodes,
  keyPrefix,
}: IResolveEnumErrorMessageKeyProps): string | null => {
  if (errorCode === null) {
    return null;
  }

  return knownCodes.has(errorCode)
    ? `${keyPrefix}${errorCode.toLowerCase()}`
    : null;
};

interface IResolveEnumErrorMessageKeyProps {
  errorCode: string | null;
  knownCodes: ReadonlySet<string>;
  keyPrefix: string;
}

const SKILL_SHARE_ERROR_CODES: ReadonlySet<string> = new Set(
  Object.values(ESkillShareErrorCode),
);

const ORGANIZATION_SKILL_ERROR_CODES: ReadonlySet<string> = new Set(
  Object.values(EOrganizationSkillErrorCode),
);

// NOTE: An axios cancellation is named "CanceledError", not "AbortError", so
// isAbortError alone would miss the axios path and turn the user's own cancel
// into an error toast.
const AXIOS_CANCELLED_ERROR_NAME = "CanceledError";

export const isCancelledSkillRequest = (error: unknown): boolean =>
  isAbortError(error) ||
  (error instanceof Error && error.name === AXIOS_CANCELLED_ERROR_NAME);

const resolveForbiddenKey = ({
  isDeleteExpected,
  isReadExpected,
}: IResolveForbiddenKeyProps): string => {
  if (isDeleteExpected) {
    return SKILL_ERROR_MESSAGE_KEYS.forbiddenDelete;
  }

  return isReadExpected
    ? SKILL_ERROR_MESSAGE_KEYS.forbiddenRead
    : SKILL_ERROR_MESSAGE_KEYS.forbidden;
};

const resolveMessageKey = ({
  statusCode,
  isDeleteExpected,
  isReadExpected,
  fallbackMessageKey,
}: IResolveMessageKeyProps): string => {
  // NOTE: 403 needs the caller's intent. The server distinguishes reading,
  // changing and deleting, and telling someone they may not CHANGE a skill when
  // they only tried to open it sends them after the wrong permission.
  if (statusCode === HTTP_STATUS_FORBIDDEN) {
    return resolveForbiddenKey({ isDeleteExpected, isReadExpected });
  }

  // NOTE: The server answers 404 for a skill that exists but is out of scope, on
  // purpose — so this message must not distinguish "gone" from "not yours".
  if (statusCode === HTTP_STATUS_NOT_FOUND) {
    return SKILL_ERROR_MESSAGE_KEYS.notFound;
  }

  if (statusCode === HTTP_STATUS_PAYLOAD_TOO_LARGE) {
    return SKILL_ERROR_MESSAGE_KEYS.tooLarge;
  }

  if (statusCode === HTTP_STATUS_TOO_MANY_REQUESTS) {
    return SKILL_ERROR_MESSAGE_KEYS.rateLimited;
  }

  if (statusCode === HTTP_STATUS_BAD_REQUEST) {
    return SKILL_ERROR_MESSAGE_KEYS.invalid;
  }

  return fallbackMessageKey;
};

// NOTE: Only the size message interpolates a value, and it needs the megabyte
// figure rather than the raw byte limit. Returning `null` for every other key
// keeps an unused `{{seconds}}`-style arg out of the translations.
const resolveMessageArgs = (messageKey: string): TSkillIssueArgs =>
  messageKey === SKILL_ERROR_MESSAGE_KEYS.tooLarge
    ? { max: Math.floor(MAX_SKILL_BUNDLE_ARCHIVE_BYTES / BYTES_PER_MEBIBYTE) }
    : null;

// NOTE: 409 is two situations sharing one status — a name already taken, and a
// version that moved under the caller. They need different copy and different
// recovery, so the caller says which one it expected.
export const resolveConflictMessageKey = ({
  isNameConflictExpected,
}: IResolveConflictMessageKeyProps): string =>
  isNameConflictExpected
    ? SKILL_ERROR_MESSAGE_KEYS.duplicateName
    : SKILL_ERROR_MESSAGE_KEYS.versionConflict;

export const sanitizeSkillError = ({
  error,
  fallbackMessageKey,
  isDeleteExpected = false,
  isReadExpected = false,
  isNameConflictExpected = false,
}: ISanitizeSkillErrorProps): ISafeSkillError => {
  // NOTE: A cancellation is the user's own action, not a failure. Reported as
  // not-an-error so a cancelled upload does not raise a toast.
  if (isCancelledSkillRequest(error)) {
    return {
      messageKey: SKILL_ERROR_MESSAGE_KEYS.cancelled,
      args: null,
      statusCode: null,
      retryAfterSeconds: null,
      isRetryable: true,
      isCancelled: true,
    };
  }

  const { statusCode, retryAfterSeconds, errorCode } = readErrorEnvelope(error);

  // NOTE: A source code is more specific than anything the status alone can say
  // — a 403 from GitHub means "no usable connection", not "you may not edit this
  // skill" — so it wins over the status map below.
  const sourceMessageKey = resolveSourceErrorMessageKey(errorCode);

  // NOTE: Same precedence, same reason: "this member already has access" and
  // "this catalog version was removed" are both 409s whose status alone would
  // render as a name conflict.
  const domainMessageKey =
    sourceMessageKey ??
    resolveEnumErrorMessageKey({
      errorCode,
      knownCodes: SKILL_SHARE_ERROR_CODES,
      keyPrefix: SKILL_SHARE_MESSAGE_KEY_PREFIX,
    }) ??
    resolveEnumErrorMessageKey({
      errorCode,
      knownCodes: ORGANIZATION_SKILL_ERROR_CODES,
      keyPrefix: ORGANIZATION_SKILL_MESSAGE_KEY_PREFIX,
    });

  const messageKey =
    domainMessageKey ??
    (statusCode === HTTP_STATUS_CONFLICT
      ? resolveConflictMessageKey({ isNameConflictExpected })
      : resolveMessageKey({
          statusCode,
          isDeleteExpected,
          isReadExpected,
          fallbackMessageKey,
        }));

  return {
    messageKey,
    args: resolveMessageArgs(messageKey),
    statusCode,
    retryAfterSeconds,
    // NOTE: NSAI-4624 — the rule itself now lives in utils/http-retry.ts, so
    // Skills and the chat document thumbnail cannot disagree about what a
    // retryable failure is.
    isRetryable: isRetryableStatus(statusCode),
    isCancelled: false,
  };
};

// NOTE: The allowlist half of the same boundary. It builds the event by NAMING
// each permitted field rather than by spreading and deleting — a spread would
// silently start emitting whatever a future caller adds, which is exactly how a
// skill name or a resource path reaches telemetry. The prop TYPES do the rest:
// `action` and `resultCode` are enums and numbers, so a caller cannot pass a
// server string through either.
export const buildSkillAnalyticsEvent = ({
  action,
  skillUuid,
  sourceType,
  resultCode,
  resourceCount,
  hasScripts,
}: IBuildSkillAnalyticsEventProps): ISkillAnalyticsEvent => {
  const event: ISkillAnalyticsEvent = { action };

  if (skillUuid) {
    event.skillUuid = skillUuid;
  }

  if (sourceType) {
    event.sourceType = sourceType;
  }

  if (typeof resultCode === "number") {
    event.resultCode = resultCode;
  }

  if (typeof resourceCount === "number") {
    event.resourceCount = resourceCount;
  }

  if (typeof hasScripts === "boolean") {
    event.hasScripts = hasScripts;
  }

  return event;
};
